import { Link, useNavigate } from "react-router-dom";
import React, { useState, useCallback, useEffect } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import { Panel, PanelBody, PanelHeader } from "../../../components/panel/panel";
import FormInput from "../../../components/CustomComponents/Input/formInput";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import { GenderDropDownSelection } from "../../../components/CustomComponents/DropdownSelections/DropDownSelections";
import Preloader from "../../../components/CustomComponents/preLoad/preLoader";
import CountryCitySelector from "../../../components/CustomComponents/DropdownSelections/CountryCitySelector";
import CustomerGroupSelector from "../../../components/CustomComponents/DropdownSelections/CustomerGroupSelector";
import ToggleSwitch from "../../../components/CustomComponents/Switchery/switchery";
import { ThreeDropdownBirthdatePicker } from "../../../components/CustomComponents/DatePicker/DatePicker";
import { useSelector } from "react-redux";
import showErrorToast, { showSuccessToast } from "../../../components/CustomComponents/toast/toast";

const initialCustomerData = {
  Cust_Fname: "",
  Cust_Lname: "",
  Cust_Address: "",
  Cust_City: "",
  Cust_Country: "UK",
  Cust_Mobile: "",
  Cust_Phone: "",
  Cust_ActivationCode: "",
  Cust_ActivationCodeCount: 0,
  Password: "",
  Cust_HouseNo: "",
  Cust_Email: "",
  Cust_PostCode: "",
  CompID: 1,
  Cust_datetime: new Date(),
  Cust_LoyaltyPoints: 0,
  SignupOrigon: "",
  CustomerStripID: "",
  Cust_Title: "",
  Cust_Gender: "",
  Cust_BusinessName: "",
  Cust_DOB: new Date(),
  Cust_MaxCredit: 1000,
  Cust_CurrentBalance: 0.0,
  Cus_Note: "",
  Cust_IsCommunicationViaEmail: false,
  Cust_IsCommunicationViaText: false,
  Cust_IsCommunicationViaPhone: false,
  Cust_Opt_In_Marketing: false,
  Cust_Reference_ID: "",
  Cust_VAT_Number: "",
  Cust_LastOrderType: "",
  Cust_AverageOrderCount: 0,
  Cust_AverageOrderSpent: 0.0,
  Cust_EnableLoyalty: false,
  Cust_Deposit: 0.0,
  Cus_LastOrderDate: null,
  Cust_TotalOrderCount: 0,
  Cust_TotalOrderSpent: 0.0,
  Cust_TotalEarnedLoyaltyPoints: 0.0,
  Cust_TotalSpentLoyaltyPoints: 0.0,
  Cust_GroupID: 0,
};

function AddCustomer() {
  const [selectedCity, setSelectedCity] = useState(null);
  const navigate = useNavigate();
  const [selectedGroup, setSelectedGroup] = useState(null);
  const compID = useSelector((state) => state.auth.user?.userData?.compID);
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState(initialCustomerData);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  

  const handleInputChange = useCallback((e, isDropdown = false) => {
    if (isDropdown) {
      const { name, value } = e;
      setCustomerData((prev) => ({
        ...prev,
        [name]: value || "",
      }));
    } else {
      const { name, value, type, checked } = e.target;
      setCustomerData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  }, []);

  const handleToggleChange = useCallback((checked, name) => {
    setCustomerData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  }, []);

  const submitCustomer = async (submitData) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_ADD_UPDATE_CUSTOMER, submitData);

      if (response.data.status) {
        showSuccessToast(response.data.message || "Customer Successfully Created");
        navigate("/customer/manageCustomer");
        setIsSubmitted(true);
      } else {
        showErrorToast(response.data.message || "Error Creating Customer");
      }
    } catch (err) {
      if (err.response) {
        showErrorToast(err.response.data.message || "Error submitting customer");
      } else if (err.request) {
        showErrorToast("No response received from server");
      } else {
        showErrorToast("Error in customer submission");
      }
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!compID) {
      showErrorToast("Company ID is missing. Please sign in again.");
      return;
    }

    if (!customerData.Cust_Fname.trim()) {
      showErrorToast("Please provide a valid first name");
      return;
    }

    if (!customerData.Cust_Email || !validateEmail(customerData.Cust_Email)) {
      showErrorToast("Please provide a valid email");
      return;
    }

    if (!customerData.Cust_Mobile?.trim()) {
      // Mobile is optional contact data
    }

    if (customerData.Cust_Country && (selectedCity === null || selectedCity === undefined || selectedCity === "")) {
      showErrorToast("Please select a city for the chosen country");
      return;
    }

    if (!selectedGroup) {
      showErrorToast("Please Select a customer group");
      return;
    }

    // SP_AddUpdateCustomer expects @Cust_City as INT (city ID), not the city name.
    const submitData = {
      ...customerData,
      Cust_Guid: "00000000-0000-0000-0000-000000000000",
      Cust_DOB: customerData.Cust_DOB || null,
      Cust_datetime: new Date().toISOString(),
      Cust_City:
        selectedCity !== null && selectedCity !== undefined && selectedCity !== ""
          ? String(selectedCity)
          : "",
      Cust_GroupID: selectedGroup,
      cus_Note: customerData.Cus_Note ?? "",
    };

    submitData.CompID = compID;
    await submitCustomer(submitData);
  };

  const handleBirthdateChange = useCallback((birthdateInfo) => {
    setCustomerData((prev) => ({
      ...prev,
      Cust_DOB: birthdateInfo.formattedDate,
    }));
  }, []);

  useEffect(() => {
    if (isSubmitted) {
      const timer = setTimeout(() => {
        setIsSubmitted(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isSubmitted]);

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/customer">Manage Customers</Link>
            </li>
            <li className="breadcrumb-item active">Add Customer</li>
          </ol>
          <h1 className="page-header mb-0">Add Customer</h1>
        </div>
        <div className="ms-auto">
          <button
            className="btn px-4"
            onClick={handleSubmit}
            disabled={loading}
            style={{ 
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: 'none',
              color: 'white',
              borderRadius: '20px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: `0 2px 8px ${themeColor}40`,
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }
            }}
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>

      <div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>General</PanelHeader>
          <PanelBody>
            <h5 className="mb-4 text-primary fw-semibold">Personal Information</h5>
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <FormInput
                  label="First Name"
                  name="Cust_Fname"
                  placeholder="Enter First Name"
                  type="text"
                  value={customerData.Cust_Fname}
                  onchange={handleInputChange}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Last Name"
                  name="Cust_Lname"
                  placeholder="Enter Last Name"
                  type="text"
                  value={customerData.Cust_Lname}
                  onchange={handleInputChange}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Email"
                  name="Cust_Email"
                  placeholder="Enter Email"
                  type="email"
                  value={customerData.Cust_Email}
                  onchange={handleInputChange}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Password"
                  name="Password"
                  placeholder="Enter Password"
                  type="password"
                  value={customerData.Password}
                  onchange={handleInputChange}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Date of Birth</label>
                <ThreeDropdownBirthdatePicker
                  onDateChange={handleBirthdateChange}
                  initialDate={customerData.Cust_DOB}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Gender</label>
                <GenderDropDownSelection
                  selectedValue={customerData.Cust_Gender}
                  onChange={(option) =>
                    setCustomerData((prev) => ({
                      ...prev,
                      Cust_Gender: option?.value || "",
                    }))
                  }
                />
              </div>
            </div>

            <h5 className="mb-4 text-primary fw-semibold">Contact Information</h5>
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <FormInput
                  label="Mobile Number"
                  name="Cust_Mobile"
                  placeholder="Enter Mobile Number"
                  type="text"
                  value={customerData.Cust_Mobile}
                  onchange={handleInputChange}
                />
              </div>
            </div>

            <h5 className="mb-4 text-primary fw-semibold">Communication Preferences</h5>
            <div className="row g-3 mb-4">
              <div className="col-md-3">
                <ToggleSwitch
                  isChecked={customerData.Cust_IsCommunicationViaEmail}
                  onToggle={(checked) => handleToggleChange(checked, "Cust_IsCommunicationViaEmail")}
                  name={"Cust_IsCommunicationViaEmail"}
                  label={"Communicate via Email"}
                  disabled={false}
                  themeColor={themeColor}
                />
              </div>
              <div className="col-md-3">
                <ToggleSwitch
                  isChecked={customerData.Cust_IsCommunicationViaText}
                  onToggle={(checked) => handleToggleChange(checked, "Cust_IsCommunicationViaText")}
                  name={"Cust_IsCommunicationViaText"}
                  label={"Communicate via Text"}
                  disabled={false}
                  themeColor={themeColor}
                />
              </div>
              <div className="col-md-3">
                <ToggleSwitch
                  isChecked={customerData.Cust_IsCommunicationViaPhone}
                  onToggle={(checked) => handleToggleChange(checked, "Cust_IsCommunicationViaPhone")}
                  name={"Cust_IsCommunicationViaPhone"}
                  label={"Communicate via Phone"}
                  disabled={false}
                  themeColor={themeColor}
                />
              </div>
              <div className="col-md-3">
                <ToggleSwitch
                  isChecked={customerData.Cust_Opt_In_Marketing}
                  onToggle={(checked) => handleToggleChange(checked, "Cust_Opt_In_Marketing")}
                  name={"Cust_Opt_In_Marketing"}
                  label={"Opt-in for Marketing"}
                  disabled={false}
                  themeColor={themeColor}
                />
              </div>
            </div>

            <h5 className="mb-4 text-primary fw-semibold">Address Information</h5>
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <FormInput
                  label="Address"
                  name="Cust_Address"
                  placeholder="Enter Address"
                  type="text"
                  value={customerData.Cust_Address}
                  onchange={handleInputChange}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="House Number"
                  name="Cust_HouseNo"
                  placeholder="Enter House Number"
                  type="text"
                  value={customerData.Cust_HouseNo}
                  onchange={handleInputChange}
                />
              </div>
            </div>

            <div className="row g-3 mb-4">
              <CountryCitySelector
                selectedCountry={customerData.Cust_Country}
                selectedCity={selectedCity}
                setSelectedCity={setSelectedCity}
                onChange={(updatedData) => {
                  setCustomerData((prev) => ({ ...prev, ...updatedData }));
                  // Clear city when country changes
                  if (updatedData.Cust_Country && !updatedData.Cust_City) {
                    setSelectedCity(null);
                  }
                }}
                setLoading={setLoading}
                isDarkMode={isDarkMode}
                selectedValueIsLabel={false}
                countryFieldName="Cust_Country"
                cityFieldName="Cust_City"
              />
            </div>

            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <FormInput
                  label="Post Code"
                  name="Cust_PostCode"
                  placeholder="Enter Post Code"
                  type="text"
                  value={customerData.Cust_PostCode}
                  onchange={handleInputChange}
                />
              </div>
            </div>

            <h5 className="mb-4 text-primary fw-semibold">Additional Information</h5>
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <FormInput
                  label="Reference ID"
                  name="Cust_Reference_ID"
                  placeholder="Enter Reference ID"
                  type="text"
                  value={customerData.Cust_Reference_ID}
                  onchange={handleInputChange}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="VAT Number"
                  name="Cust_VAT_Number"
                  placeholder="Enter VAT Number"
                  type="text"
                  value={customerData.Cust_VAT_Number}
                  onchange={handleInputChange}
                />
              </div>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <label className="form-label">Groups</label>
                <CustomerGroupSelector
                  selectedGroup={selectedGroup}
                  setSelectedGroup={setSelectedGroup}
                  onChange={(value) => setSelectedGroup(value)}
                  setLoading={setLoading}
                />
              </div>
            </div>
          </PanelBody>
        </Panel>
      </div>
    </div>
  );
}

export default AddCustomer;